/**
 * NonLivingGenerator - Procedural generation for non-living specimens
 * Creates crystals, dust particles, bubbles, and other non-living objects
 */

import Phaser from 'phaser';
import type { NonLivingTemplate } from '@/types';
import { SimplexNoise } from '../noise/SimplexNoise';

export interface NonLivingConfig {
  /** Size of the specimen */
  size: number;
  /** Random seed for consistent generation */
  seed?: number;
  /** Color hue (0-360) */
  hue?: number;
}

export interface RenderedNonLiving {
  container: Phaser.GameObjects.Container;
  template: NonLivingTemplate;
  size: number;
}

/**
 * NonLivingGenerator creates procedural visuals for non-living specimens
 */
export class NonLivingGenerator {
  private scene: Phaser.Scene;
  private noise: SimplexNoise;

  constructor(scene: Phaser.Scene, seed: number = Date.now()) {
    this.scene = scene;
    this.noise = new SimplexNoise(seed);
  }

  /**
   * Generate a non-living specimen
   */
  public generate(template: NonLivingTemplate, config: NonLivingConfig): RenderedNonLiving {
    const container = this.scene.add.container(0, 0);

    switch (template.id) {
      case 'crystal':
        this.renderCrystal(container, config);
        break;
      case 'dust_particle':
        this.renderDustParticle(container, config);
        break;
      case 'water_bubble':
        this.renderWaterBubble(container, config);
        break;
      case 'pollen_grain':
        this.renderPollenGrain(container, config);
        break;
      case 'oil_droplet':
        this.renderOilDroplet(container, config);
        break;
      default:
        // Generic irregular shape for unknown types
        this.renderDustParticle(container, config);
    }

    return {
      container,
      template,
      size: config.size,
    };
  }

  /**
   * Render a crystal (geometric faceted shape)
   */
  private renderCrystal(container: Phaser.GameObjects.Container, config: NonLivingConfig): void {
    const { size, hue = 200, seed = 0 } = config;
    const graphics = this.scene.add.graphics();

    // Crystal is made of geometric facets
    const facetCount = 5 + Math.floor(this.noise.noise2D(seed, 0) * 3);
    const baseAngle = Math.PI * 2 / facetCount;

    // Main crystal body color
    const mainColor = Phaser.Display.Color.HSLToColor(hue / 360, 0.3, 0.7);
    const highlightColor = Phaser.Display.Color.HSLToColor(hue / 360, 0.2, 0.9);

    // Draw multiple facets
    for (let i = 0; i < facetCount; i++) {
      const angle1 = baseAngle * i - Math.PI / 2;
      const angle2 = baseAngle * (i + 1) - Math.PI / 2;

      const radiusVariation = 0.8 + this.noise.noise2D(seed + i, seed) * 0.4;
      const radius = size * 0.5 * radiusVariation;

      const x1 = Math.cos(angle1) * radius;
      const y1 = Math.sin(angle1) * radius;
      const x2 = Math.cos(angle2) * radius;
      const y2 = Math.sin(angle2) * radius;

      // Facet color varies by angle (simulates light refraction)
      const lightness = 0.5 + Math.sin(angle1) * 0.3;
      const facetColor = i % 2 === 0 ? mainColor : Phaser.Display.Color.HSLToColor(hue / 360, 0.25, lightness);

      graphics.fillStyle(facetColor.color, 0.8);
      graphics.beginPath();
      graphics.moveTo(0, 0);
      graphics.lineTo(x1, y1);
      graphics.lineTo(x2, y2);
      graphics.closePath();
      graphics.fill();

      // Edge highlight
      graphics.lineStyle(1, highlightColor.color, 0.6);
      graphics.lineBetween(x1, y1, x2, y2);
    }

    // Add internal refraction lines
    graphics.lineStyle(1, highlightColor.color, 0.3);
    for (let i = 0; i < 3; i++) {
      const lineAngle = baseAngle * i * 2 - Math.PI / 3;
      const lineLength = size * 0.3;
      graphics.lineBetween(
        Math.cos(lineAngle) * lineLength * 0.2,
        Math.sin(lineAngle) * lineLength * 0.2,
        Math.cos(lineAngle) * lineLength,
        Math.sin(lineAngle) * lineLength
      );
    }

    // Add sparkle highlight
    const sparkle = this.scene.add.graphics();
    sparkle.fillStyle(0xffffff, 0.8);
    sparkle.fillCircle(-size * 0.15, -size * 0.2, size * 0.05);
    sparkle.fillStyle(0xffffff, 0.4);
    sparkle.fillCircle(-size * 0.1, -size * 0.15, size * 0.03);

    container.add([graphics, sparkle]);
  }

  /**
   * Render a dust particle (irregular organic-looking shape)
   */
  private renderDustParticle(container: Phaser.GameObjects.Container, config: NonLivingConfig): void {
    const { size, hue = 35, seed = 0 } = config;
    const graphics = this.scene.add.graphics();

    // Irregular shape using noise
    const points: Phaser.Geom.Point[] = [];
    const pointCount = 12;

    for (let i = 0; i < pointCount; i++) {
      const angle = (i / pointCount) * Math.PI * 2;
      const noiseVal = this.noise.noise2D(
        seed + Math.cos(angle) * 2,
        seed + Math.sin(angle) * 2
      );
      const radius = size * 0.3 + size * 0.2 * noiseVal;

      points.push(new Phaser.Geom.Point(
        Math.cos(angle) * radius,
        Math.sin(angle) * radius
      ));
    }

    // Fill with brownish/gray color
    const dustColor = Phaser.Display.Color.HSLToColor(hue / 360, 0.3, 0.4);
    graphics.fillStyle(dustColor.color, 0.9);
    graphics.beginPath();
    graphics.moveTo(points[0].x, points[0].y);

    for (let i = 1; i < points.length; i++) {
      graphics.lineTo(points[i].x, points[i].y);
    }
    graphics.closePath();
    graphics.fill();

    // Add some texture/speckling
    graphics.fillStyle(0x000000, 0.2);
    for (let i = 0; i < 5; i++) {
      const x = (this.noise.noise2D(seed + i * 10, 0) - 0.5) * size * 0.4;
      const y = (this.noise.noise2D(seed, i * 10) - 0.5) * size * 0.4;
      const speckSize = 1 + this.noise.noise2D(seed + i, seed + i) * 2;
      graphics.fillCircle(x, y, speckSize);
    }

    container.add(graphics);
  }

  /**
   * Render a water bubble (spherical with highlight)
   */
  private renderWaterBubble(container: Phaser.GameObjects.Container, config: NonLivingConfig): void {
    const { size } = config;
    const graphics = this.scene.add.graphics();
    const radius = size * 0.4;

    // Outer ring (surface tension)
    graphics.lineStyle(2, 0x90cdf4, 0.6);
    graphics.strokeCircle(0, 0, radius);

    // Inner fill (transparent)
    graphics.fillStyle(0x90cdf4, 0.15);
    graphics.fillCircle(0, 0, radius);

    // Highlight (light reflection)
    const highlight = this.scene.add.graphics();
    highlight.fillStyle(0xffffff, 0.7);

    // Main highlight
    highlight.fillEllipse(-radius * 0.3, -radius * 0.3, radius * 0.4, radius * 0.25);

    // Secondary highlight
    highlight.fillStyle(0xffffff, 0.4);
    highlight.fillCircle(-radius * 0.1, -radius * 0.5, radius * 0.1);

    // Bottom refraction
    graphics.lineStyle(1, 0x90cdf4, 0.3);
    graphics.beginPath();
    graphics.arc(0, 0, radius * 0.8, Math.PI * 0.7, Math.PI * 0.9);
    graphics.stroke();

    container.add([graphics, highlight]);
  }

  /**
   * Render a pollen grain (textured sphere)
   */
  private renderPollenGrain(container: Phaser.GameObjects.Container, config: NonLivingConfig): void {
    const { size, hue = 50, seed = 0 } = config;
    const graphics = this.scene.add.graphics();
    const radius = size * 0.35;

    // Main body (yellowish)
    const mainColor = Phaser.Display.Color.HSLToColor(hue / 360, 0.7, 0.6);
    graphics.fillStyle(mainColor.color, 1);
    graphics.fillCircle(0, 0, radius);

    // Textured surface (spiky bumps)
    const spikeColor = Phaser.Display.Color.HSLToColor(hue / 360, 0.6, 0.5);
    graphics.fillStyle(spikeColor.color, 1);

    const spikeCount = 12;
    for (let i = 0; i < spikeCount; i++) {
      const angle = (i / spikeCount) * Math.PI * 2;
      const noiseVal = this.noise.noise2D(seed + i, seed);
      const spikeLength = radius * 0.15 + radius * 0.1 * noiseVal;
      const spikeWidth = radius * 0.08;

      const x = Math.cos(angle) * radius;
      const y = Math.sin(angle) * radius;
      const tipX = Math.cos(angle) * (radius + spikeLength);
      const tipY = Math.sin(angle) * (radius + spikeLength);

      // Draw spike as triangle
      const perpAngle = angle + Math.PI / 2;
      const baseX1 = x + Math.cos(perpAngle) * spikeWidth;
      const baseY1 = y + Math.sin(perpAngle) * spikeWidth;
      const baseX2 = x - Math.cos(perpAngle) * spikeWidth;
      const baseY2 = y - Math.sin(perpAngle) * spikeWidth;

      graphics.beginPath();
      graphics.moveTo(baseX1, baseY1);
      graphics.lineTo(tipX, tipY);
      graphics.lineTo(baseX2, baseY2);
      graphics.closePath();
      graphics.fill();
    }

    // Inner pattern (darker spots)
    const spotColor = Phaser.Display.Color.HSLToColor(hue / 360, 0.5, 0.4);
    graphics.fillStyle(spotColor.color, 0.5);
    for (let i = 0; i < 6; i++) {
      const angle = (i / 6) * Math.PI * 2 + Math.PI / 6;
      const dist = radius * 0.5;
      const spotRadius = radius * 0.1;
      graphics.fillCircle(
        Math.cos(angle) * dist,
        Math.sin(angle) * dist,
        spotRadius
      );
    }

    // Highlight
    graphics.fillStyle(0xffffff, 0.3);
    graphics.fillEllipse(-radius * 0.3, -radius * 0.3, radius * 0.3, radius * 0.2);

    container.add(graphics);
  }

  /**
   * Render an oil droplet (spherical with rainbow refraction)
   */
  private renderOilDroplet(container: Phaser.GameObjects.Container, config: NonLivingConfig): void {
    const { size } = config;
    const graphics = this.scene.add.graphics();
    const radius = size * 0.4;

    // Outer edge
    graphics.lineStyle(2, 0x333333, 0.5);
    graphics.strokeCircle(0, 0, radius);

    // Rainbow refraction effect (multiple colored arcs)
    const colors = [0xff6b6b, 0xffd93d, 0x6bcb77, 0x4d96ff, 0xc77dff];
    const arcWidth = radius * 0.15;

    for (let i = 0; i < colors.length; i++) {
      const arcRadius = radius * 0.3 + i * arcWidth * 0.8;
      graphics.lineStyle(arcWidth, colors[i], 0.3);
      graphics.beginPath();
      graphics.arc(
        0, 0,
        arcRadius,
        -Math.PI * 0.7 + i * 0.1,
        -Math.PI * 0.3 - i * 0.1
      );
      graphics.stroke();
    }

    // Fill with semi-transparent
    graphics.fillStyle(0x444444, 0.2);
    graphics.fillCircle(0, 0, radius);

    // Bright highlight
    const highlight = this.scene.add.graphics();
    highlight.fillStyle(0xffffff, 0.6);
    highlight.fillEllipse(-radius * 0.25, -radius * 0.35, radius * 0.35, radius * 0.2);

    highlight.fillStyle(0xffffff, 0.3);
    highlight.fillCircle(-radius * 0.1, -radius * 0.5, radius * 0.08);

    container.add([graphics, highlight]);
  }

  /**
   * Update noise seed for new variations
   */
  public setSeed(seed: number): void {
    this.noise = new SimplexNoise(seed);
  }
}
